import { FC, memo, useMemo, useRef, useCallback, useEffect } from 'react';

import {
  useLazyGetUserSuggestedLiveShowsQuery,
  useLazyGetUserSuggestedRecordedShowsQuery,
} from '@redux/queries/user';
import {
  useMyTeamSuggestedShows,
  useMayLoadMoreMyTeamSuggested,
  useNonMyTeamSuggestedShows,
  useMayLoadMoreNonMyTeamSuggested,
  useSuggestedRecordedShows,
  useMayLoadMoreSuggestedRecorded,
} from '@redux/selectors/shows';
import Loader from '@components/loader';
import ShowCard from '@components/show/card';
import { emptyText } from '@utils/constants';
import { useScroll } from '@hooks/useScroll';

import { ShowsContainer, EmptyState } from './styled';

const SuggestedFeed: FC = () => {
  const showsContainerRef = useRef<HTMLDivElement>(null);

  const [
    getUserSuggestedMyTeamLiveShows,
    {
      isFetching: isFetchingMyTeamLiveShows,
      isSuccess: isSuccessMyTeamLiveShows,
      isUninitialized: isUninitializedMyTeamLiveShows,
      // isError: isErrorMyTeamLiveShows,
    },
  ] = useLazyGetUserSuggestedLiveShowsQuery();
  const [
    getUserSuggestedNonMyTeamLiveShows,
    {
      isFetching: isFetchingNonMyTeamLiveShows,
      isSuccess: isSuccessNonMyTeamLiveShows,
      // isError: isErrorNonMyTeamLiveShows,
    },
  ] = useLazyGetUserSuggestedLiveShowsQuery();
  const [
    getUserSuggestedRecordedShows,
    {
      isFetching: isFetchingRecordedShows,
      isSuccess: isSuccessRecordedShows,
      // isError: isErrorRecordedShows,
    },
  ] = useLazyGetUserSuggestedRecordedShowsQuery();
  const myTeamShowsIds = useMyTeamSuggestedShows();
  const mayLoadMoreMyTeamShows = useMayLoadMoreMyTeamSuggested();
  const nonMyTeamShowsIds = useNonMyTeamSuggestedShows();
  const mayLoadMoreNonMyTeamShows = useMayLoadMoreNonMyTeamSuggested();
  const recordedShowsIds = useSuggestedRecordedShows();
  const mayLoadMoreRecordedShows = useMayLoadMoreSuggestedRecorded();

  // const anyError = useMemo(
  //   () =>
  //     isErrorMyTeamLiveShows ||
  //     isErrorNonMyTeamLiveShows ||
  //     isErrorRecordedShows,
  //   [isErrorMyTeamLiveShows, isErrorNonMyTeamLiveShows, isErrorRecordedShows],
  // );
  // const anySuccess = useMemo(
  //   () =>
  //     isSuccessMyTeamLiveShows ||
  //     isSuccessNonMyTeamLiveShows ||
  //     isSuccessRecordedShows,
  //   [
  //     isSuccessMyTeamLiveShows,
  //     isSuccessNonMyTeamLiveShows,
  //     isSuccessRecordedShows,
  //   ],
  // );

  const suggestedFeedIds = useMemo(() => {
    if (mayLoadMoreMyTeamShows) {
      return [...myTeamShowsIds];
    } else if (mayLoadMoreNonMyTeamShows) {
      return [...myTeamShowsIds, ...nonMyTeamShowsIds];
    } else {
      return [...myTeamShowsIds, ...nonMyTeamShowsIds, ...recordedShowsIds];
    }
  }, [
    myTeamShowsIds,
    mayLoadMoreMyTeamShows,
    nonMyTeamShowsIds,
    mayLoadMoreNonMyTeamShows,
    recordedShowsIds,
  ]);

  const onLoadMore = useCallback(() => {
    if (mayLoadMoreMyTeamShows) {
      getUserSuggestedMyTeamLiveShows({
        fromMyTeam: true,
        lastId: myTeamShowsIds[myTeamShowsIds.length - 1],
        isRefresh: false,
      });
    } else if (mayLoadMoreNonMyTeamShows) {
      getUserSuggestedNonMyTeamLiveShows({
        fromMyTeam: false,
        lastId: nonMyTeamShowsIds[nonMyTeamShowsIds.length - 1],
        isRefresh: false,
      });
    } else if (mayLoadMoreRecordedShows) {
      getUserSuggestedRecordedShows({
        lastId: recordedShowsIds[recordedShowsIds.length - 1],
        isRefresh: false,
      });
    }
  }, [
    mayLoadMoreMyTeamShows,
    mayLoadMoreNonMyTeamShows,
    mayLoadMoreRecordedShows,
    myTeamShowsIds,
    nonMyTeamShowsIds,
    recordedShowsIds,
    getUserSuggestedMyTeamLiveShows,
    getUserSuggestedNonMyTeamLiveShows,
    getUserSuggestedRecordedShows,
  ]);

  useEffect(() => {
    if (isUninitializedMyTeamLiveShows) {
      getUserSuggestedMyTeamLiveShows({
        fromMyTeam: true,
        isRefresh: true,
      });
    }
  }, [isUninitializedMyTeamLiveShows, getUserSuggestedMyTeamLiveShows]);

  useEffect(() => {
    if (
      isSuccessMyTeamLiveShows &&
      !mayLoadMoreMyTeamShows &&
      !isSuccessNonMyTeamLiveShows
    ) {
      getUserSuggestedNonMyTeamLiveShows({
        fromMyTeam: false,
        isRefresh: true,
      });
    }
  }, [
    isSuccessMyTeamLiveShows,
    mayLoadMoreMyTeamShows,
    getUserSuggestedNonMyTeamLiveShows,
    isSuccessNonMyTeamLiveShows,
  ]);

  useEffect(() => {
    if (
      isSuccessNonMyTeamLiveShows &&
      !mayLoadMoreNonMyTeamShows &&
      !isSuccessRecordedShows
    ) {
      getUserSuggestedRecordedShows({
        isRefresh: true,
      });
    }
  }, [
    isSuccessNonMyTeamLiveShows,
    mayLoadMoreNonMyTeamShows,
    getUserSuggestedRecordedShows,
    isSuccessRecordedShows,
  ]);

  useScroll({ onLoadMore, containerRef: showsContainerRef });

  return (
    <ShowsContainer $isEmpty={!suggestedFeedIds.length} ref={showsContainerRef}>
      {!suggestedFeedIds.length ? (
        <EmptyState>{emptyText.shows}</EmptyState>
      ) : (
        suggestedFeedIds.map((id: string) => <ShowCard key={id} showId={id} />)
      )}

      {!!suggestedFeedIds.length &&
        (isFetchingMyTeamLiveShows ||
          isFetchingNonMyTeamLiveShows ||
          isFetchingRecordedShows) && <Loader size={'large'} />}
    </ShowsContainer>
  );
};

export default memo(SuggestedFeed);
